import React, { useState } from "react"
import { Link } from "gatsby"
import Image from '../components/image';
import Layout from "../components/layout"
import SEO from "../components/seo"
import { Grid, Avatar, Tooltip, Button, List, ListItem, Typography, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from "@material-ui/core";
import { Assistant } from "@material-ui/icons";
import { DropzoneArea } from 'material-ui-dropzone';
import { useSnackbar } from 'notistack';
import { ClockLoader, MoonLoader } from "react-spinners";
import { CircularProgressbarWithChildren as CircularProgressbar } from 'react-circular-progressbar';
import ProgressProvider from '../components/progressProvider';
import MoreInfo from '../components/moreInfo';

import 'react-circular-progressbar/dist/styles.css';
import '../styles/face-similarity.scss';
import allChars from '../data/characters.json';
import faceGIF from '../images/face-recognition.gif';
import avatarGIF from '../images/avatar.gif';
import avatarJPG from '../images/avatar.jpg';

const intendedChars = ['eleven', 'mike', 'will', 'dustin', 'lucas', 'max', 'nancy', 'jonathan', 'steve', 'joyce', 'jim'];
let chars = allChars.filter((c) => (intendedChars.includes(c.id)) ? c : null)
chars = chars.sort((a, b) => intendedChars.indexOf(a.id) - intendedChars.indexOf(b.id))

const onClick = (files, tries, setOpen, setTries, notif) => {
  const file = files[0];
  if (!file) {
    notif("No files selected!", { variant: "error" });
    return;
  }
  if (tries > 3) {
    notif("You've tried more than 3 times", { variant: "warning" });
    notif("Come back just a few seconds later", { variant: "default" });
    if (tries === 4) {
      setTimeout(() => {
        setTries(0);
      }, 10000);
    }
    return;
  }
  setOpen(true)
}

const doProcess = async (file, setOpen, setLoading, setData, setTries, notif) => {
  notif("Please wait ...", { variant: "info" });
  setLoading(true);
  const scrollInterval = setInterval(() => {
    const loading = document.getElementById("loading");
    if(loading){
      clearInterval(scrollInterval);      
      window.scrollTo(0, loading.offsetTop);
    }
  }, 10);
  if (typeof (window) !== undefined) {
    setOpen(false);
    const formData = new FormData();
    formData.append("file", file);
    const fsAPI = window.fsAPI;
    try {
      const res = await fetch(fsAPI, { method: "POST", credentials: "same-origin", body: formData })
      const resJSON = await res.json();
      const { message, result, status } = resJSON;
      if (status.code === 200) {
        result.result = (result.result || []).map((r, idx) => ({ similarity: Math.round(r * 10000) / 100, ...chars[idx] })).sort((a, b) => b.similarity - a.similarity);
        setData(result);
        setTries((tries) => tries > 4 ? tries : tries + 1);
        notif("Processed Successfully", { variant: "success" });
      }
      else {
        (message || []).forEach((msg) => {
          notif(msg, { variant: "error" });
        });
      }
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
      notif("Something went wrong!", { variant: "error" });
      notif("Try again later", { variant: "default" });
    }
    finally {
      return;
    }
  }
  notif("No API FOUND!", { variant: "error" });
}

const Render = () => {
  const [files, setFiles] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [tries, setTries] = useState(0);
  const { enqueueSnackbar: notif } = useSnackbar();
  const mediaURL = (typeof (window) !== `undefined`) ? window.mediaURL : "";
  const { image_path, result } = (data || {});

  return (
    <div id="FaceSimilarity">
      <div className="head">
        <img src={faceGIF} alt="stranger things face similarity" />
        <h1>Check out the <b>face-similarity</b> matching process and find out who you look like</h1>
      </div>
      <div>
        <h2>You'll be matched with one of these characters:</h2>
        <Grid container component="ul" spacing={1}>
          {
            chars.map((char) => (
              <Grid key={char.row} item component="li" xs={4} sm={3} md={2} lg={1} xl={1}>
                <Tooltip title={char.name} arrow placement="top">
                  <Avatar style={{ width: 100, height: 100, boxShadow: "0 0 6px #6b6b6b" }}>
                    <Image name={char.avatar} style={{ width: "100%", height: "100%" }} />
                  </Avatar>
                </Tooltip>
              </Grid>
            ))
          }
        </Grid>
        <div><b>Your Face Identity is similar to</b> ... !</div>
        <div><b>Guess Who? Just do the process to make sure!</b></div>
      </div>
      <br />
      <h3 className="ai-ml"><Assistant /> Powered By Artificial Intelligence & Machine Learning</h3>
      {
        !loading && !data &&
        (
          <>
            <div className="help">
              <Grid container spacing={4}>
                <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                  <img className="gif" src={avatarGIF} alt="avatar" />
                </Grid>
                <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                  <img className="gif" src={avatarJPG} alt="avatar" />
                </Grid>
              </Grid>
            </div>
            <div className="tips">
              <Typography variant="h4" component="h3">Tips:</Typography>
              <List>
                <ListItem>Your photo should <u>not</u> have much background <b>textures</b></ListItem>
                <ListItem>The dimension of your photo is better to be <u>more than</u>&nbsp;<b>200x200</b></ListItem>
                <ListItem>Your photo <u>doesn't need</u> to be cropped to contain just the <b>face</b>. The process will recognise the face!</ListItem>
                <ListItem>Your photo should contain <b>one face</b> because the process will consider the first face that is recognised!</ListItem>
              </List>
            </div>
            <div className="dropzone-wrapper">
              <DropzoneArea
                dropzoneText="Drag and drop an image here or click"
                filesLimit={1}
                maxFileSize={3000000}
                dropzoneClass="dropzone"
                dropzoneParagraphClass="text"
                previewGridClasses={{ container: "container", item: "item", image: "image" }}
                acceptedFiles={["image/*"]}
                onChange={(files) => setFiles(files)}
              />
            </div>
            <Button
              fullWidth
              size="large"
              color="secondary"
              variant="contained"
              onClick={() => onClick(files, tries, setOpen, setTries, notif)}
            >
              Run Process
            </Button>
          </>
        )
      }
      {
        loading &&
        (
          <div id="loading" className="loading">
            <ClockLoader
              color="#C11B1F"
              size={250}
              loading
            />
            <div className="running">
              <MoonLoader size={20} />
              <h3 style={{ textAlign: "center" }}>Running On Server</h3>
              <MoonLoader size={20} />
            </div>
            <Typography variant="h6">Please Wait</Typography>
            <Typography variant="subtitle1">It might take a minute or two</Typography>
          </div>
        )
      }
      {
        !loading && data &&
        (
          <div className="result">
            <Button fullWidth color="secondary" variant="outlined" onClick={() => setData(null)}>Try Another One</Button>
            <br />
            <br />
            <Typography variant="subtitle1">
              Make sure the <b>red square</b> is around your intended face (in the left image)
            </Typography>
            <Typography variant="subtitle2">
              Otherwise, you must use a better photo so that our agent could detect the face
            </Typography>
            <br />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={12} md={4} lg={5}>
                <img src={`${mediaURL}/${image_path}`} alt="detected" />
              </Grid>
              <Grid item xs={12} sm={12} md={8} lg={7}>
                <div className="first">
                  <ProgressProvider valueStart={0} valueEnd={result[0].similarity}>
                    {
                      (value) => (
                        <CircularProgressbar className="result-circle" strokeWidth={4} background backgroundPadding={2} value={value} maxValue={100}>
                          <Tooltip title={result[0].name} arrow placement="top">
                            <Avatar className="avatar">
                              <Image name={result[0].avatar} style={{ width: "100%", height: "100%" }} />
                            </Avatar>
                          </Tooltip>
                        </CircularProgressbar>
                      )
                    }
                  </ProgressProvider>
                  <Typography variant="h4">{Math.round(result[0].similarity)}%</Typography>
                </div>
                <div className="others">
                  <Grid container spacing={2} justify="center">
                    {
                      (result.slice(1)).map((res) => (
                        <Grid key={res.name} item xs={6} sm={6} md={3}>
                          <ProgressProvider valueStart={0} valueEnd={res.similarity}>
                            {
                              (value) => (

                                <CircularProgressbar className="result-circle" strokeWidth={4} background backgroundPadding={2} value={value} maxValue={100}>
                                  <Tooltip title={res.name} arrow placement="top">
                                    <Avatar className="avatar">
                                      <Image name={res.avatar} style={{ width: "100%", height: "100%" }} />
                                    </Avatar>
                                  </Tooltip>
                                </CircularProgressbar>
                              )
                            }
                          </ProgressProvider>
                          <Typography variant="h5">{Math.round(res.similarity)}%</Typography>
                        </Grid>
                      ))
                    }
                  </Grid>
                </div>
              </Grid>
            </Grid>
          </div>
        )
      }
      <br />
      <br />
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth="md"
        fullWidth
      >
        <DialogTitle id="alert-dialog-title">{"Run the Face Similarity process?"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            You are about to upload your photo to get the face matching results.
            <br />
            The photo will be collected <b>safely</b> on our servers in order to be utilized in the future machine learning datasets.
            This will be used only for educational or progressing purposes. (just the face part)
            <br />
            The resulting photo will be deleted within 24 hours from our servers.
            <br />
            <b>Do you agree with these terms?</b>
            <br />
            <br />
            (to continue, you need to agree!)
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} color="primary">
            No
          </Button>
          <Button onClick={() => doProcess(files[0], setOpen, setLoading, setData, setTries, notif)} color="secondary">
            Yes, Of Course
          </Button>
        </DialogActions>
      </Dialog>
    </div >
  );
}

const description = `Do you wanna know which stranger things character you look like the most? upload your photo contains a face, then get the similarity result instantly.`;
const keywords = [
  "stranger things stuffs",
  "stranger things",
  "nerdy face similarity",
  "face similarity checking",
  "ST",
  "Eleven",
  "Mike",
  "Will",
  "Dustin",
  "Lucas",
  "Mad Max",
  "Nancy",
  "Jonathan",
  "Steve",
  "Joyce",
  "Jim Hopper",
]

const mi = {
  why: `<ul>
    <li>To have some fun</li>
    <li>To make some learnings -- our learning, not machine learning :)</li>
    <li>To know which character our face is matched the most with</li>
  </ul>`,
  method: `<ul>
    <li>Machine learning approach (Convolutional Neural Network)</li>
  </ul>
  <hr />
  <div>Stages:</div>
  <br />
  <ul>
  <li>Gather dataset (11 classes)</li>
  <li>Write feature obtainer function (HOG + LBP + GB)</li>
  <li>Find the best network architecture</li>
  <li>Train Data</li>
  <li>Achieve model</li>
  </ul>
  <hr />
  <div>
    You can find slim version of the dataset I've gathered in kaggle: <a href="https://www.kaggle.com/alirezakay/stranger-things-faces-dataset-grayscale">dataset</a>
  </div>
  <br />
  <div>
  <iframe width="100%" height="300" src="https://www.youtube-nocookie.com/embed/pr9beE9msWw" frameborder="0" allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>
  </div>
  `,
  accuracy: `<div>
    ~ 78 percent
  </div>`,
  version: `<div>v0.1.8</div>`,
}

const FaceSimilarity = () => (
  <Layout navNumber={3}>
    <SEO
      title="Find Which Characters Your Face Look Like The Most - Face Similarity"
      description={description}
      keywords={keywords.join(",")}
    />
    <Render />
    <div>
      <MoreInfo
        why={mi.why}
        method={mi.method}
        accuracy={mi.accuracy}
        version={mi.version}
      />
    </div>
    <Link to="/">Go back to the homepage</Link>
  </Layout>
)

export default FaceSimilarity
